/**
 * Composable for consistent URL matching across navigation components
 * Provides different matching strategies with proper locale handling
 */
export function useUrlMatching() {
  const route = useRoute();
  const { $localeRootPath } = useNuxtApp();

  /**
   * Exact path matching - most precise, recommended for menus
   * @param {string} targetPath - The path to match against
   * @returns {boolean} - True if current route exactly matches target
   */
  const isExactMatch = (targetPath) => {
    if (!route?.path || !targetPath) return false;

    const currentPath = route.path;
    const normalizedTarget = $localeRootPath(targetPath);

    return currentPath === normalizedTarget;
  };

  /**
   * Path includes matching - for section-level navigation
   * @param {string} targetPath - The path to check inclusion for
   * @returns {boolean} - True if current route includes target path
   */
  const isPathIncluded = (targetPath) => {
    if (!route?.path || !targetPath) return false;

    const currentPath = route.path;
    const normalizedTarget = $localeRootPath(targetPath);

    // Don't match root path (/) or empty paths to prevent always-active state
    if (normalizedTarget === '/' || normalizedTarget === '') return false;

    return currentPath.includes(normalizedTarget);
  };

  /**
   * Path starts with matching - for hierarchical navigation
   * @param {string} targetPath - The path to check start with
   * @returns {boolean} - True if current route starts with target path
   */
  const isPathStartsWith = (targetPath) => {
    if (!route?.path || !targetPath) return false;

    const currentPath = route.path;
    const normalizedTarget = $localeRootPath(targetPath);

    return currentPath.startsWith(normalizedTarget);
  };

  /**
   * Flexible active route checker with configurable matching strategy
   * @param {string} targetPath - The path to match against
   * @param {Object} options - Configuration options
   * @param {string} options.strategy - 'exact' | 'includes' | 'startsWith' (default: 'exact')
   * @returns {boolean} - True if route is active according to strategy
   */
  const isActiveRoute = (targetPath, options = {}) => {
    const { strategy = 'exact' } = options;

    switch (strategy) {
      case 'includes':
        return isPathIncluded(targetPath);
      case 'startsWith':
        return isPathStartsWith(targetPath);
      case 'exact':
      default:
        return isExactMatch(targetPath);
    }
  };

  /**
   * Get active class based on route matching
   * @param {string} targetPath - The path to match against
   * @param {string} activeClass - CSS class to return if active
   * @param {Object} options - Configuration options (same as isActiveRoute)
   * @returns {string|undefined} - Active class if route matches, undefined otherwise
   */
  const getActiveClass = (targetPath, activeClass, options = {}) => {
    return isActiveRoute(targetPath, options) ? activeClass : undefined;
  };

  return {
    isExactMatch,
    isPathIncluded,
    isPathStartsWith,
    isActiveRoute,
    getActiveClass,
  };
}
